import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff, ShieldCheck, ChevronLeft, AlertCircle, CheckCircle2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /conta/senha — authenticated change-password screen.
 *
 * On success, stamps `password_manually_changed=true` server-side so the
 * startup admin-seed never overrides this choice (see server.py).
 */
export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  React.useEffect(() => {
    if (isAuthenticated === false) navigate('/login');
  }, [isAuthenticated, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next !== confirm) {
      setError('A confirmação não confere com a nova senha');
      return;
    }
    if (next.length < 8) {
      setError('A nova senha deve ter no m?nimo 8 caracteres');
      return;
    }
    if (next === current) {
      setError('A nova senha deve ser diferente da atual');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true }
      );
      setSuccess(true);
      toast.success('Senha alterada com sucesso!');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="change-password-page">
      <SEOHead
        title="Alterar senha | BuscaDoutor"
        description="Área restrita para alteração de senha no BuscaDoutor."
        canonicalUrl="/conta/senha"
        robots="noindex, nofollow"
      />
      <Navbar />
      <section className="py-12 md:py-16 bg-gradient-to-br from-[#F4FAF8] via-white to-[#F4FAF8]">
        <div className="container-app max-w-xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#59706A] hover:text-primary mb-6 text-sm transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Voltar
          </Link>

          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.06)] p-7 md:p-9">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Lock className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1A2421] leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Alterar Senha
                </h1>
                <p className="text-sm text-[#59706A] mt-1">
                  {user?.email && <>Conta: <strong>{user.email}</strong></>}
                </p>
              </div>
            </div>

            {success && (
              <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-5" data-testid="success-banner">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Senha alterada com sucesso. A partir de agora ela <strong>não será mais redefinida</strong> automaticamente nas reinicializações do backend.</span>
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-pwd">Senha atual</Label>
                <div className="relative">
                  <Input
                    id="current-pwd"
                    type={showCurrent ? 'text' : 'password'}
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="h-11 pr-11"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    tabIndex={-1}
                  >
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-pwd">Nova senha (m?nimo 8 caracteres)</Label>
                <div className="relative">
                  <Input
                    id="new-pwd"
                    type={showNext ? 'text' : 'password'}
                    minLength={8}
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="h-11 pr-11"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNext((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    tabIndex={-1}
                  >
                    {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-pwd">Confirmar nova senha</Label>
                <Input
                  id="confirm-pwd"
                  type={showNext ? 'text' : 'password'}
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="h-11"
                  data-testid="confirm-password-input"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="error-banner">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !current || next.length < 8 || next !== confirm}
                className="w-full h-12 bg-primary hover:bg-[#2D6D5D] text-white rounded-full"
                data-testid="submit-change-password"
              >
                {loading ? 'Salvando...' : 'Alterar senha'}
              </Button>
            </form>

            <p className="text-xs text-[#59706A]/80 mt-6 inline-flex items-start gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
              Apos a primeira alteracao manual, a senha fica congelada e nunca sera substituida por valores default em deploys futuros.
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
